import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Phone, MessageCircle, Mail, MapPin, Sparkles } from 'lucide-react';
import { mockData } from '../../data/mock';

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hello SAX! I'd like to learn more about your services.");
    const whatsappUrl = `https://wa.me/${mockData.company.whatsapp.replace(/\s+/g, '').replace(/^\+/, '')}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleCallClick = () => {
    window.open(`tel:${mockData.company.phone}`, '_self');
  };

  const navItemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0 },
    hover: { 
      scale: 1.05,
      transition: { duration: 0.2 }
    }
  };

  const logoVariants = {
    hover: {
      scale: 1.1,
      rotate: [0, -1, 1, -1, 0],
      transition: { duration: 0.6 }
    }
  };

  const buttonVariants = {
    hover: {
      scale: 1.05,
      boxShadow: "0 10px 25px rgba(255, 92, 138, 0.3)",
      transition: { duration: 0.3 }
    },
    tap: { scale: 0.95 }
  };

  return (
    <>
      {/* Enhanced Top Utility Bar */}
      <motion.div 
        className="relative bg-gradient-to-r from-[#1F3C88] via-[#2d4ba3] to-[#FF5C8A] text-white py-3 overflow-hidden"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute -top-1 -left-10 w-20 h-20 bg-white/10 rounded-full blur-xl"
            animate={{
              x: [0, 100, 0],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute -bottom-1 -right-10 w-16 h-16 bg-[#FF8C42]/20 rounded-full blur-lg"
            animate={{
              x: [0, -80, 0],
              scale: [1, 1.3, 1],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>

        <div className="container mx-auto px-4 flex justify-between items-center text-sm relative z-10">
          <motion.div 
            className="flex items-center space-x-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <motion.div 
              className="flex items-center space-x-2 group cursor-pointer"
              whileHover={{ scale: 1.05 }}
            >
              <motion.div
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
              >
                <MapPin size={16} className="text-[#FF8C42] group-hover:text-white transition-colors" />
              </motion.div>
              <span className="font-medium group-hover:text-[#FF8C42] transition-colors">
                {mockData.company.location}
              </span>
            </motion.div>
          </motion.div>

          <motion.div 
            className="flex items-center space-x-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <motion.button
              onClick={handleCallClick}
              className="flex items-center space-x-2 group relative overflow-hidden px-3 py-1 rounded-full"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                className="absolute inset-0 bg-white/10 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                layoutId="button-bg"
              />
              <motion.div
                whileHover={{ rotate: [0, -10, 10, 0] }}
                transition={{ duration: 0.4 }}
              >
                <Phone size={16} className="text-[#FF8C42] group-hover:text-white transition-colors relative z-10" />
              </motion.div>
              <span className="font-medium group-hover:text-[#FF8C42] transition-colors relative z-10">
                {mockData.company.phone}
              </span>
            </motion.button>

            <motion.button
              onClick={handleWhatsAppClick}
              className="flex items-center space-x-2 group relative overflow-hidden px-3 py-1 rounded-full"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                className="absolute inset-0 bg-white/10 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                layoutId="whatsapp-bg"
              />
              <motion.div
                whileHover={{ rotate: [0, 15, -15, 0] }}
                transition={{ duration: 0.4 }}
              >
                <MessageCircle size={16} className="text-[#FF8C42] group-hover:text-white transition-colors relative z-10" />
              </motion.div>
              <span className="font-medium group-hover:text-[#FF8C42] transition-colors relative z-10">
                WhatsApp Support
              </span>
            </motion.button>
          </motion.div>
        </div>

        {/* Animated Border */}
        <motion.div
          className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#FF8C42] to-[#FF5C8A]"
          initial={{ width: "0%" }}
          animate={{ width: "100%" }}
          transition={{ duration: 2, delay: 0.5 }}
        />
      </motion.div>

      {/* Enhanced Main Header */}
      <motion.header
        className={`sticky top-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-lg shadow-2xl py-2 border-b border-gray-100' 
            : 'bg-white shadow-lg py-4'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        {/* Professional Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-gradient-to-r from-[#1F3C88]/10 via-transparent to-[#FF5C8A]/10" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="flex items-center justify-between">
            {/* Professional Enhanced Logo Section */}
            <Link to="/" className="flex items-center group">
              <motion.div
                className="relative"
                variants={logoVariants}
                whileHover="hover"
              >
                {/* Professional Logo Container */}
                <motion.div className="relative">
                  {/* Premium Background Frame */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-br from-white via-blue-50 to-pink-50 rounded-2xl shadow-lg border border-white/50 backdrop-blur-sm"
                    animate={{
                      boxShadow: [
                        "0 4px 20px rgba(31, 60, 136, 0.1)",
                        "0 8px 30px rgba(255, 92, 138, 0.15)",
                        "0 4px 20px rgba(31, 60, 136, 0.1)"
                      ]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
                  
                  {/* Animated Border */}
                  <motion.div
                    className="absolute inset-0 rounded-2xl"
                    style={{
                      background: "linear-gradient(45deg, #1F3C88, #FF5C8A, #FF8C42, #1F3C88)",
                      backgroundSize: "400% 400%",
                      padding: "2px"
                    }}
                    animate={{
                      backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <div className="w-full h-full rounded-2xl bg-white" />
                  </motion.div>

                  {/* Logo with Enhanced Effects */}
                  <motion.div className="relative z-10 p-3">
                    {/* Premium Glow Effect */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-[#1F3C88]/20 via-[#FF5C8A]/20 to-[#FF8C42]/20 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      animate={{
                        scale: [1, 1.2, 1],
                        rotate: [0, 5, -5, 0]
                      }}
                      transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                    
                    <motion.img
                      src="https://customer-assets.emergentagent.com/job_sax-advisory/artifacts/1bisi1kh_transparent.png"
                      alt="SAX Project Management Services"
                      className={`relative z-10 transition-all duration-500 ${
                        isScrolled ? 'h-10' : 'h-12'
                      } w-auto drop-shadow-lg`}
                      whileHover={{
                        scale: 1.1,
                        rotate: [0, -2, 2, 0],
                        filter: "drop-shadow(0 10px 20px rgba(31, 60, 136, 0.3))"
                      }}
                      transition={{ duration: 0.6 }}
                    />

                    {/* Multiple Sparkle Effects */}
                    <motion.div
                      className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100"
                      animate={{
                        rotate: 360,
                        scale: [0, 1.2, 0]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      <Sparkles size={14} className="text-[#FF8C42]" />
                    </motion.div>

                    <motion.div
                      className="absolute -bottom-1 -left-1 opacity-0 group-hover:opacity-100"
                      animate={{
                        rotate: -360,
                        scale: [0, 1, 0]
                      }}
                      transition={{
                        duration: 2.5,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 0.5
                      }}
                    >
                      <Sparkles size={12} className="text-[#FF5C8A]" />
                    </motion.div>
                  </motion.div>
                </motion.div>

              </motion.div>
            </Link>

            {/* Right Side: Navigation + CTA Group */}
            <div className="hidden lg:flex items-center space-x-2">
              {/* Compact Navigation */}
              <nav className="flex items-center space-x-1">
              {mockData.navigation.map((item, index) => (
                <motion.div
                  key={item.name}
                  className="relative"
                  variants={navItemVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: index * 0.1 }}
                  onMouseEnter={() => {
                    if (item.submenu) setActiveSubmenu(item.name);
                    setHoveredItem(item.name);
                  }}
                  onMouseLeave={() => {
                    setActiveSubmenu(null);
                    setHoveredItem(null);
                  }}
                >
                  <Link
                    to={item.path}
                    className="relative group"
                  >
                    <motion.div
                      className="flex items-center space-x-1 px-2 py-1 rounded-lg transition-all duration-300"
                      whileHover="hover"
                      variants={navItemVariants}
                    >
                      {/* Background Hover Effect */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-[#1F3C88]/10 to-[#FF5C8A]/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        layoutId={hoveredItem === item.name ? "nav-bg" : undefined}
                      />
                      
                      <span className={`font-semibold transition-all duration-300 relative z-10 ${
                        location.pathname === item.path 
                          ? 'text-[#1F3C88] font-bold' 
                          : 'text-gray-700 group-hover:text-[#1F3C88]'
                      }`}>
                        {item.name}
                      </span>
                      
                      {item.submenu && (
                        <motion.div
                          animate={{ rotate: activeSubmenu === item.name ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <ChevronDown size={16} className={`transition-colors duration-300 ${
                            location.pathname === item.path || hoveredItem === item.name
                              ? 'text-[#1F3C88]' 
                              : 'text-gray-500'
                          }`} />
                        </motion.div>
                      )}
                    </motion.div>

                    {/* Active Page Indicator */}
                    {location.pathname === item.path && (
                      <motion.div
                        className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-full"
                        layoutId="active-indicator"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.3 }}
                      />
                    )}
                  </Link>

                  {/* Enhanced Mega Menu */}
                  <AnimatePresence>
                    {item.submenu && activeSubmenu === item.name && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 5, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="absolute top-full left-0 mt-3 w-72 bg-white/95 backdrop-blur-lg shadow-2xl rounded-2xl border border-gray-100 py-4 overflow-hidden"
                      >
                        {/* Menu Background Gradient */}
                        <div className="absolute inset-0 bg-gradient-to-br from-white via-blue-50/30 to-pink-50/30" />
                        
                        {/* Menu Header */}
                        <div className="relative z-10 px-6 pb-3 mb-3 border-b border-gray-100">
                          <h3 className="text-sm font-bold text-[#1F3C88] uppercase tracking-wider">
                            Our Services
                          </h3>
                        </div>

                        {/* Menu Items */}
                        <div className="relative z-10 space-y-1">
                          {item.submenu.map((subItem, subIndex) => (
                            <motion.div
                              key={subItem.name}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: subIndex * 0.05 }}
                            >
                              <Link
                                to={subItem.path}
                                className="group flex items-center px-6 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-[#1F3C88]/5 hover:to-[#FF5C8A]/5 transition-all duration-300 rounded-lg mx-2"
                              >
                                <motion.div
                                  className="w-2 h-2 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-full mr-4 opacity-60 group-hover:opacity-100 transition-opacity"
                                  whileHover={{ scale: 1.5 }}
                                />
                                <span className="font-medium group-hover:text-[#1F3C88] transition-colors">
                                  {subItem.name}
                                </span>
                                <motion.div
                                  className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
                                  whileHover={{ x: 5 }}
                                >
                                  <ChevronDown size={14} className="rotate-[-90deg] text-[#FF5C8A]" />
                                </motion.div>
                              </Link>
                            </motion.div>
                          ))}
                        </div>

                        {/* Menu Footer */}
                        <div className="relative z-10 px-6 pt-4 mt-4 border-t border-gray-100">
                          <Link
                            to="/request-service"
                            className="block text-center py-2 px-4 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] text-white rounded-lg text-sm font-semibold hover:shadow-lg transition-shadow"
                          >
                            Request Service
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
              </nav>

              {/* Special Premium CTA Button - Now in same container */}
              <motion.div
                className="relative ml-4"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.6 }}
              >
                {/* Floating Particles Effect */}
                <motion.div
                  className="absolute -inset-4 opacity-30"
                  animate={{
                    rotate: 360
                  }}
                  transition={{
                    duration: 20,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                >
                  {[...Array(6)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute w-1 h-1 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-full"
                      style={{
                        top: `${15 + Math.sin(i * 60 * Math.PI / 180) * 25}px`,
                        left: `${15 + Math.cos(i * 60 * Math.PI / 180) * 25}px`,
                      }}
                      animate={{
                        scale: [0, 1, 0],
                        opacity: [0, 1, 0]
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        delay: i * 0.5,
                        ease: "easeInOut"
                      }}
                    />
                  ))}
                </motion.div>

                {/* Premium Button Container */}
                <motion.div
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                  className="relative"
                >
                  {/* Outer Glow Ring */}
                  <motion.div
                    className="absolute -inset-1 rounded-2xl opacity-75"
                    style={{
                      background: "linear-gradient(45deg, #FF5C8A, #FF8C42, #1F3C88, #FF5C8A)",
                      backgroundSize: "400% 400%"
                    }}
                    animate={{
                      backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />

                  {/* Main Button */}
                  <Link
                    to="/request-service"
                    className="relative block"
                  >
                    {/* Premium Background */}
                    <motion.div
                      className="relative overflow-hidden rounded-xl px-8 py-4 font-bold text-sm text-white shadow-2xl"
                      style={{
                        background: "linear-gradient(135deg, #FF5C8A 0%, #FF8C42 50%, #FF5C8A 100%)",
                        backgroundSize: "200% 200%"
                      }}
                      animate={{
                        backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"]
                      }}
                      transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      {/* Premium Inner Glow */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20"
                        animate={{
                          x: ["-100%", "100%"]
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      />

                      {/* Premium Content */}
                      <motion.div 
                        className="relative z-10 flex items-center space-x-3"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                      >
                        {/* Animated Crown Icon */}
                        <motion.div
                          animate={{
                            rotate: [0, 10, -10, 0],
                            scale: [1, 1.1, 1]
                          }}
                          transition={{
                            duration: 2.5,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                        >
                          <Sparkles size={18} className="drop-shadow-lg" />
                        </motion.div>

                        <span className="font-extrabold tracking-wide drop-shadow-lg">
                          Request Service
                        </span>

                        {/* Animated Arrow */}
                        <motion.div
                          animate={{
                            x: [0, 3, 0],
                            rotate: [0, 15, 0]
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                        >
                          <ChevronDown size={16} className="rotate-[-90deg] drop-shadow-lg" />
                        </motion.div>
                      </motion.div>

                      {/* Premium Shine Effect */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12"
                        initial={{ x: "-100%" }}
                        animate={{ x: "100%" }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: "easeInOut",
                          delay: 1
                        }}
                      />

                      {/* Floating Sparkles */}
                      <motion.div
                        className="absolute top-1 right-2"
                        animate={{
                          y: [-2, -8, -2],
                          opacity: [0, 1, 0],
                          scale: [0, 1, 0]
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      >
                        <Sparkles size={12} className="text-white/80" />
                      </motion.div>

                      <motion.div
                        className="absolute bottom-1 left-2"
                        animate={{
                          y: [2, 8, 2],
                          opacity: [0, 1, 0],
                          scale: [0, 1, 0]
                        }}
                        transition={{
                          duration: 2.5,
                          repeat: Infinity,
                          ease: "easeInOut",
                          delay: 1
                        }}
                      >
                        <Sparkles size={10} className="text-white/60" />
                      </motion.div>
                    </motion.div>
                  </Link>
                </motion.div>

                {/* Premium Floating Text */}
                <motion.div
                  className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-xs text-[#1F3C88] font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  animate={{
                    y: [0, -2, 0]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  Get Started Today
                </motion.div>
              </motion.div>
            </div>

            {/* Enhanced Mobile Menu Button */}
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden relative p-3 text-gray-600 hover:text-[#1F3C88] transition-colors rounded-xl hover:bg-gray-50"
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                {isOpen ? <X size={24} /> : <Menu size={24} />}
              </motion.div>
            </motion.button>
          </div>
        </div>

        {/* Enhanced Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0, y: -20 }}
              animate={{ opacity: 1, height: 'auto', y: 0 }}
              exit={{ opacity: 0, height: 0, y: -20 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="lg:hidden bg-white/95 backdrop-blur-lg border-t border-gray-100 shadow-2xl"
            >
              {/* Mobile Menu Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white to-pink-50/50" />
              
              <div className="container mx-auto px-4 py-6 relative z-10">
                <motion.nav 
                  className="space-y-2"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    hidden: {},
                    visible: {
                      transition: {
                        staggerChildren: 0.1
                      }
                    }
                  }}
                >
                  {mockData.navigation.map((item, index) => (
                    <motion.div 
                      key={item.name}
                      variants={{
                        hidden: { opacity: 0, x: -20 },
                        visible: { opacity: 1, x: 0 }
                      }}
                    >
                      <Link
                        to={item.path}
                        className={`block group relative overflow-hidden px-4 py-4 rounded-xl transition-all duration-300 ${
                          location.pathname === item.path 
                            ? 'bg-gradient-to-r from-[#1F3C88]/10 to-[#FF5C8A]/10 text-[#1F3C88] font-bold' 
                            : 'text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50/30 hover:text-[#1F3C88]'
                        }`}
                        onClick={() => setIsOpen(false)}
                      >
                        <motion.div
                          className="flex items-center justify-between"
                          whileTap={{ scale: 0.98 }}
                        >
                          <span className="font-semibold text-lg">{item.name}</span>
                          {location.pathname === item.path && (
                            <motion.div
                              className="w-2 h-2 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-full"
                              layoutId="mobile-active"
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                            />
                          )}
                        </motion.div>
                        
                        {/* Mobile Submenu */}
                        {item.submenu && (
                          <motion.div 
                            className="mt-3 ml-4 space-y-2"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                          >
                            {item.submenu.map((subItem) => (
                              <Link
                                key={subItem.name}
                                to={subItem.path}
                                className="block py-2 text-gray-600 hover:text-[#1F3C88] text-sm font-medium transition-colors"
                                onClick={() => setIsOpen(false)}
                              >
                                • {subItem.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </Link>
                    </motion.div>
                  ))}
                  
                  {/* Mobile CTA Button */}
                  <motion.div
                    variants={{
                      hidden: { opacity: 0, y: 20 },
                      visible: { opacity: 1, y: 0 }
                    }}
                    className="pt-4"
                  >
                    <Link
                      to="/request-service"
                      className="block text-center bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-shadow"
                      onClick={() => setIsOpen(false)}
                    >
                      <motion.span
                        className="flex items-center justify-center space-x-2"
                        whileTap={{ scale: 0.98 }}
                      >
                        <span>Request Service</span>
                        <Sparkles size={18} />
                      </motion.span>
                    </Link>
                  </motion.div>
                </motion.nav>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
    </>
  );
};